// frontend/src/components/billing/billing-details.tsx
// Read DESIGN.md before modifying this file.
// Phase 2 (spec 2026-06-18): native billing email + bill-to name & address.
// Field order mirrors Stripe's AddressElement (name → country → address →
// city → postal), with the billing email directly under the name. The whole
// form saves as ONE POST, and the Save button only appears once a field has
// actually changed from the loaded values — no per-field buttons. Reads
// GET /organizations/:orgId/billing/billing-details via Query and renders an
// inline Retry on failure — never the global error page.
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useFetch } from '@/hooks/use-fetch';
import { useBillingDetailsQuery, useBillingDetailsInvalidate } from './queries';
import { AddressAutocomplete } from './address-autocomplete';

interface Form {
  name: string;
  email: string;
  country: string;
  line1: string;
  city: string;
  state: string;
  postalCode: string;
}

const EMPTY_FORM: Form = {
  name: '',
  email: '',
  country: '',
  line1: '',
  city: '',
  state: '',
  postalCode: '',
};

// The billing email is REQUIRED — a blank value can't be saved (it would wipe
// the address Stripe invoices to). Returns the inline error for a value, or
// null when it's a well-formed address. Checked on blur and again on Save.
function emailErrorFor(value: string): string | null {
  if (value.trim() === '') return 'Billing email is required.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
    return 'Enter a valid billing email address.';
  return null;
}

export function BillingDetails({ orgId }: { orgId: string }) {
  // The save POST deliberately stays on raw fetchWithAuth: its 400 handling
  // inspects the class-validator message body (email errors surface inline on
  // the field, everything else toasts the backend copy) — detail the AppError
  // thrown by the Query wrapper does not carry.
  const fetchWithAuth = useFetch();
  const detailsQuery = useBillingDetailsQuery(orgId);
  const invalidateDetails = useBillingDetailsInvalidate(orgId);
  // `initial` is the last-saved snapshot; `form` is the live edit. The Save
  // button shows only when they diverge.
  const [initial, setInitial] = useState<Form>(EMPTY_FORM);
  const [form, setForm] = useState<Form>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  const loading = detailsQuery.isPending || detailsQuery.isFetching;
  const error = detailsQuery.isError;

  const set = <K extends keyof Form>(key: K, value: Form[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const isDirty = (Object.keys(form) as (keyof Form)[]).some(
    (k) => form[k] !== initial[k],
  );

  // Reset the snapshot + live form from every successful fetch (mount and the
  // post-save refetch). Keyed on dataUpdatedAt, not the data reference, so a
  // refetch that returns deep-equal data (structural sharing keeps the
  // reference) still resets the form like the old load() did.
  useEffect(() => {
    const data = detailsQuery.data;
    if (!data) return;
    const loaded: Form = {
      name: data.name ?? '',
      email: data.email ?? '',
      country: data.address?.country ?? '',
      line1: data.address?.line1 ?? '',
      city: data.address?.city ?? '',
      state: data.address?.state ?? '',
      postalCode: data.address?.postalCode ?? '',
    };
    setInitial(loaded);
    setForm(loaded);
  }, [detailsQuery.data, detailsQuery.dataUpdatedAt]);

  const save = useCallback(async () => {
    // Flag a missing / malformed email inline before the round-trip — no toast.
    const emailIssue = emailErrorFor(form.email);
    if (emailIssue) {
      setEmailError(emailIssue);
      return;
    }
    setEmailError(null);
    setSaving(true);
    try {
      const res = await fetchWithAuth(
        `/organizations/${orgId}/billing/billing-details`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form),
        },
      );
      if (res.ok) {
        toast.success('Billing details saved.');
        await invalidateDetails();
      } else if (res.status === 400) {
        // Backend rejected a field (email / address). Surface it on the email
        // input when it's the offender, otherwise fall back to a toast.
        const body = (await res.json().catch(() => null)) as {
          code?: string;
          message?: string | string[];
        } | null;
        const msg = Array.isArray(body?.message)
          ? body.message.join(' ')
          : body?.message ?? '';
        if (/email/i.test(msg)) {
          setEmailError('Enter a valid billing email address.');
        } else {
          toast.error(msg || 'Some billing details were invalid.');
        }
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [form, fetchWithAuth, orgId, invalidateDetails]);

  return (
    <div className="space-y-3">
      <h3 className="text-lg font-semibold tracking-tight">Billing Details</h3>
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Loading billing details...
        </div>
      ) : error ? (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Unable to load billing details.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void detailsQuery.refetch()}
          >
            Retry
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="billing-name">Organization legal name</Label>
            <Input
              id="billing-name"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="billing-email">Billing email</Label>
            <Input
              id="billing-email"
              type="email"
              value={form.email}
              aria-invalid={emailError ? true : undefined}
              onChange={(e) => {
                set('email', e.target.value);
                // Clear the error as they type so a valid fix removes the red
                // state live; re-checked on blur / Save.
                if (emailError) setEmailError(null);
              }}
              onBlur={(e) => {
                // Billing email is required — emptying it then leaving the
                // field snaps back to the last saved value instead of clearing.
                if (e.target.value.trim() === '') {
                  set('email', initial.email);
                  setEmailError(null);
                  return;
                }
                setEmailError(emailErrorFor(e.target.value));
              }}
            />
            {emailError && (
              <p role="alert" className="text-sm text-destructive">
                {emailError}
              </p>
            )}
          </div>
          <AddressAutocomplete
            value={{
              country: form.country,
              line1: form.line1,
              city: form.city,
              state: form.state,
              postalCode: form.postalCode,
            }}
            onChange={(addr) => setForm((prev) => ({ ...prev, ...addr }))}
          />
          {isDirty && (
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setForm(initial);
                  setEmailError(null);
                }}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button size="sm" onClick={save} disabled={saving}>
                {saving && <Loader2 className="size-3.5 animate-spin" />}
                Save
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
